import {
  AbsenceStatusCombobox,
  AbsenceTypeCombobox,
  DatePicker,
  LeaderGroupCombobox,
  toast,
  useHideBasedOnLeaderGroup,
  UserCombobox,
} from '@/components'
import {
  ExportFormat as ExportFormatEnum,
  ExportOption as ExportOptionEnum,
} from '@/modules/exports'
import {
  ExportCard,
  ExportField,
  ExportFormatField,
  ExportMethodField,
} from '@/modules/exports/components/export-form'
import { EXPORT_ABSENCE_REPORT_PER_USER } from '@/modules/exports/mutations'
import {
  absenceRequestStatuses,
  uiToApiAbsenceStatus,
} from '@/modules/timesheets/absence-status.mapping'
import { useAdmin, useFormWatch, useLeaderGroupsSuspense } from '@/utils'
import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslate } from '@ur-solutions/react-hooks'
import { endOfMonth, startOfMonth, subMonths } from 'date-fns'
import { Activity } from 'react'
import { Controller, useForm } from 'react-hook-form'
import z from 'zod'
import { createAbsenceReportSchema } from './util'

const AbsenceReportUserSchema = createAbsenceReportSchema(
  z.object({
    dateRange: z.object({
      from: z.date(),
      to: z.date(),
    }),
    leaderGroup: z.string(),
    users: z.string().array(),
    absenceTypes: z.string().array(),
    requestStatuses: z.enum(absenceRequestStatuses).array(),
  }),
  ['SAVE', 'MAIL'],
  [
    'PDF',
    'EXCEL',
    'VISMA',
    'POWEROFFICE',
    'TWENTYFOUR_SEVEN_OFFICE',
    'TRIPLETEX',
  ]
)

type AbsenceReportUserForm = z.infer<typeof AbsenceReportUserSchema>

const EXCLUDED_FORMATS = [
  ExportFormatEnum.EXCEL_FLAT,
  ExportFormatEnum.PDF_SALARY,
]

export function AbsenceReportPerUserCard() {
  const translations = useTranslate({
    header: 'settings.absence.reports.absence-report-user',

    dateRange: 'settings.absence.reports.date-range',
    leaderGroup: 'settings.absence.reports.leader-group',
    users: 'settings.absence.reports.users',
    absenceType: 'absences.filters.absence-type',
    status: 'common.status',

    allUsers: ['settings.absence.reports.all-users', { n: 0 }],
    allAbsenceTypes: 'settings.absence.reports.all-absence-types',
    allStatuses: 'settings.absence.reports.all-statuses',
    generateReport: 'settings.absence.reports.generate-report',

    error: 'exports.error-generic',
  })

  const isAdmin = useAdmin()
  const leaderGroups = useLeaderGroupsSuspense()

  const form = useForm({
    resolver: zodResolver(AbsenceReportUserSchema),
    defaultValues: {
      dateRange: {
        from: startOfMonth(subMonths(new Date(), 1)),
        to: endOfMonth(subMonths(new Date(), 1)),
      },
      leaderGroup: '',
      users: [],
      absenceTypes: [],
      requestStatuses: ['approved'],
      exportOption: 'SAVE',
      exportFormat: 'PDF',
    },
  })

  const [exportReport, { loading }] = useMutation(
    EXPORT_ABSENCE_REPORT_PER_USER,
    {
      onCompleted(data) {
        toast.export(data.exportAbsenceReportPerUser.exportId)
      },
      onError(err) {
        console.error(err)
        toast.error(translations.error)
      },
    }
  )

  const leaderGroup = useFormWatch(form, 'leaderGroup')

  const hideUserItem = useHideBasedOnLeaderGroup(leaderGroup, leaderGroups)

  async function handleSubmit(data: AbsenceReportUserForm) {
    await exportReport({
      variables: {
        dateStart: data.dateRange.from.toISOString(),
        dateEnd: data.dateRange.to.toISOString(),
        leaderGroup: data.leaderGroup || null,
        users: data.users,
        absenceTypes: data.absenceTypes,
        requestStatuses: (data.requestStatuses.length
          ? data.requestStatuses
          : absenceRequestStatuses
        ).map(status => uiToApiAbsenceStatus[status]),
        exportOption: data.exportOption,
        exportFormat: data.exportFormat,
      },
    })
  }

  return (
    <ExportCard
      title={translations.header}
      loading={loading}
      submitLabel={translations.generateReport}
      onSubmit={() => void form.handleSubmit(handleSubmit)()}
    >
      <Controller
        control={form.control}
        name="dateRange"
        render={({ field }) => (
          <ExportField label={translations.dateRange}>
            <DatePicker
              {...field}
              mode="range"
              format="PP"
              onChange={value => {
                if (value === null) return
                field.onChange(value)
              }}
            />
          </ExportField>
        )}
      />

      <Activity mode={leaderGroups.hasAccess ? 'visible' : 'hidden'}>
        <Controller
          control={form.control}
          name="leaderGroup"
          render={({ field }) => (
            <ExportField label={translations.leaderGroup}>
              <LeaderGroupCombobox
                {...field}
                mode="single"
                clearable={isAdmin}
                onChange={value => {
                  field.onChange(value)
                  form.setValue('users', [])
                }}
              />
            </ExportField>
          )}
        />
      </Activity>

      <Controller
        control={form.control}
        name="users"
        render={({ field }) => (
          <ExportField label={translations.users} wide>
            <UserCombobox
              {...field}
              mode="multiple"
              placeholder={items => translations.allUsers({ n: items.length })}
              hideItem={hideUserItem}
            />
          </ExportField>
        )}
      />

      <Controller
        control={form.control}
        name="absenceTypes"
        render={({ field }) => (
          <ExportField label={translations.absenceType}>
            <AbsenceTypeCombobox
              {...field}
              mode="multiple"
              placeholder={translations.allAbsenceTypes}
            />
          </ExportField>
        )}
      />

      <Controller
        control={form.control}
        name="requestStatuses"
        render={({ field }) => (
          <ExportField label={translations.status}>
            <AbsenceStatusCombobox
              {...field}
              mode="multiple"
              placeholder={translations.allStatuses}
            />
          </ExportField>
        )}
      />

      <Controller
        control={form.control}
        name="exportOption"
        render={({ field }) => (
          <ExportMethodField
            value={ExportOptionEnum[field.value]}
            onChange={field.onChange}
          />
        )}
      />

      <Controller
        control={form.control}
        name="exportFormat"
        render={({ field }) => (
          <ExportFormatField
            value={ExportFormatEnum[field.value]}
            exclude={EXCLUDED_FORMATS}
            onChange={field.onChange}
          />
        )}
      />
    </ExportCard>
  )
}
